import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Compass, Plus, Heart, User, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

export const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const { user, userProfile } = useAuth();

  const pathname = location.pathname;

  // Don't render on Admin routes or during auth actions
  if (pathname.includes('/admin') || pathname.includes('/clm-master-vault') || pathname.includes('/auth/action')) {
    return null;
  }

  const isHomeActive = pathname === '/' || pathname === '/fr' || pathname === '/en';
  const isMapActive = pathname.includes('/map') || pathname.includes('/carte');
  const isPostActive = pathname.includes('/post-property') || pathname.includes('/deposer-annonce');
  const isFavoritesActive = pathname.includes('/saved') || pathname.includes('/favoris');
  const isAccountActive =
    pathname.includes('/dashboard') ||
    pathname.includes('/tableau-de-bord') ||
    pathname.includes('/profile') ||
    pathname.includes('/signin') ||
    pathname.includes('/connexion');

  return (
    <nav
      aria-label="Mobile Navigation"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200/90 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] px-2 py-1.5 pb-safe"
    >
      <div className="max-w-md mx-auto grid grid-cols-5 items-center justify-items-center">
        {/* 1. Home / Explore */}
        <Link
          to={isFr ? '/fr' : '/en'}
          className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-colors ${
            isHomeActive ? 'text-[#094426]' : 'text-slate-500 hover:text-slate-900'
          }`}
        >
          <Home className={`w-5 h-5 ${isHomeActive ? 'stroke-[2.5]' : 'stroke-[1.75]'}`} />
          <span className={`text-[10px] mt-0.5 font-medium ${isHomeActive ? 'font-bold text-[#094426]' : ''}`}>
            {isFr ? 'Accueil' : 'Home'}
          </span>
        </Link>

        {/* 2. Cadastral Map */}
        <Link
          to={isFr ? '/fr/carte' : '/en/map'}
          className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-colors ${
            isMapActive ? 'text-[#094426]' : 'text-slate-500 hover:text-slate-900'
          }`}
        >
          <Compass className={`w-5 h-5 ${isMapActive ? 'stroke-[2.5]' : 'stroke-[1.75]'}`} />
          <span className={`text-[10px] mt-0.5 font-medium ${isMapActive ? 'font-bold text-[#094426]' : ''}`}>
            {isFr ? 'Carte' : 'Map'}
          </span>
        </Link>

        {/* 3. Center Elevated Gold Plus Button (Post Property) */}
        <Link
          to={isFr ? '/fr/deposer-annonce' : '/en/post-property'}
          className="flex flex-col items-center justify-center -mt-5 group"
          aria-label={isFr ? 'Déposer une annonce' : 'Post Free Ad'}
        >
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 via-amber-400 to-yellow-300 text-slate-950 flex items-center justify-center shadow-lg shadow-amber-500/30 border-2 border-white transform transition-transform group-active:scale-95 group-hover:scale-105">
            <Plus className="w-6 h-6 stroke-[3]" />
          </div>
          <span className="text-[10px] mt-1 font-extrabold text-slate-800">
            {isFr ? 'Publier' : 'Post Ad'}
          </span>
        </Link>

        {/* 4. Favorites & Saved Offline Properties */}
        <Link
          to={isFr ? '/fr/favoris' : '/en/saved'}
          className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-colors ${
            isFavoritesActive ? 'text-[#094426]' : 'text-slate-500 hover:text-slate-900'
          }`}
        >
          <Heart className={`w-5 h-5 ${isFavoritesActive ? 'stroke-[2.5] fill-[#094426]' : 'stroke-[1.75]'}`} />
          <span className={`text-[10px] mt-0.5 font-medium ${isFavoritesActive ? 'font-bold text-[#094426]' : ''}`}>
            {isFr ? 'Favoris' : 'Saved'}
          </span>
        </Link>

        {/* 5. Account / Dashboard */}
        <Link
          to={
            user || userProfile
              ? isFr
                ? '/fr/tableau-de-bord'
                : '/en/dashboard'
              : isFr
              ? '/fr/connexion'
              : '/en/signin'
          }
          className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-colors ${
            isAccountActive ? 'text-[#094426]' : 'text-slate-500 hover:text-slate-900'
          }`}
        >
          <User className={`w-5 h-5 ${isAccountActive ? 'stroke-[2.5]' : 'stroke-[1.75]'}`} />
          <span className={`text-[10px] mt-0.5 font-medium ${isAccountActive ? 'font-bold text-[#094426]' : ''}`}>
            {user || userProfile ? (isFr ? 'Compte' : 'Account') : isFr ? 'Connexion' : 'Sign In'}
          </span>
        </Link>
      </div>
    </nav>
  );
};
